import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { createEmptyModel, parseMinecraftGeometry } from '../utils/blockbenchParser';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Hook principal para el editor:
 * - Gestiona el estado local del proyecto (modelo, behaviors)
 * - Persiste automáticamente al backend
 * - NUNCA sobrescribe el estado local cuando llegan cambios del backend
 */
export const useEditorProject = (projectId) => {
  const [project, setProject] = useState(null);
  const [model, setModel] = useState(null);
  const [behaviors, setBehaviors] = useState({ general: {}, events: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  
  const initializedRef = useRef(false);
  const saveTimerRef = useRef(null);

  // ============================================================
  // HISTORIAL Undo/Redo
  // Guardamos snapshots { model, behaviors } al hacer un cambio.
  // Máximo 50 estados (~memoria acotada).
  // El puntero apunta al estado ACTUAL dentro del array.
  // ============================================================
  const historyRef = useRef({ stack: [], pointer: -1 });
  const skipHistoryRef = useRef(false);
  const [historyVersion, setHistoryVersion] = useState(0); // bump para forzar re-render de canUndo/canRedo
  const MAX_HISTORY = 50;

  const pushHistory = useCallback((snapshotModel, snapshotBehaviors) => {
    const h = historyRef.current;
    // Cortar el historial futuro si estamos en medio de él
    const truncated = h.stack.slice(0, h.pointer + 1);
    truncated.push({
      model: snapshotModel,
      behaviors: snapshotBehaviors,
    });
    if (truncated.length > MAX_HISTORY) truncated.shift();
    historyRef.current = { stack: truncated, pointer: truncated.length - 1 };
    setHistoryVersion((v) => v + 1);
  }, []);

  // Carga inicial (SOLO UNA VEZ)
  useEffect(() => {
    if (!projectId || initializedRef.current) return;

    const loadProject = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/projects/${projectId}`);
        const proj = response.data;
        setProject(proj);

        // Inicializar modelo
        const geo = proj.geometry;
        let loadedModel;
        if (!geo || Object.keys(geo).length === 0) {
          loadedModel = createEmptyModel();
        } else if (geo.cubes) {
          loadedModel = geo;
        } else if (geo['minecraft:geometry']) {
          loadedModel = parseMinecraftGeometry(geo);
        } else {
          loadedModel = createEmptyModel();
        }
        // La textura viene ahora del proyecto (raíz), no del modelo.
        // Se inyecta en el modelo para que ModelPainter/ThreeViewer puedan leerla.
        if (proj.texture) {
          loadedModel = { ...loadedModel, texture: proj.texture };
        }
        setModel(loadedModel);

        // Inicializar behaviors (compatibilidad con formato antiguo)
        const bhv = proj.behaviors;
        let initialBehaviors;
        if (Array.isArray(bhv)) {
          initialBehaviors = { general: {}, events: [] };
        } else if (bhv && typeof bhv === 'object') {
          initialBehaviors = { general: bhv.general || {}, events: bhv.events || [] };
        } else {
          initialBehaviors = { general: {}, events: [] };
        }
        setBehaviors(initialBehaviors);

        // Snapshot inicial en el historial (marca el estado limpio de partida)
        historyRef.current = {
          stack: [{ model: loadedModel, behaviors: initialBehaviors }],
          pointer: 0,
        };

        initializedRef.current = true;
      } catch (error) {
        console.error('Error loading project:', error);
      } finally {
        setLoading(false);
      }
    };

    loadProject();
  }, [projectId]);

  // Auto-save con debounce
  const scheduleSave = useCallback(() => {
    setDirty(true);
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      await saveNow();
    }, 1500);
  }, []);

  const saveNow = useCallback(async () => {
    if (!projectId || !initializedRef.current) return;
    setSaving(true);
    try {
      // Extraer texture del modelo y enviarla como campo separado del proyecto.
      // El pintor almacena la textura editada dentro del modelo (model.texture),
      // pero el backend/exportador la espera en project.texture al nivel raíz.
      const currentModel = modelRef.current || {};
      const { texture: paintedTexture, ...modelWithoutTexture } = currentModel;
      const payload = {
        geometry: modelWithoutTexture,
        behaviors: behaviorsRef.current,
      };
      if (paintedTexture) {
        payload.texture = paintedTexture;
      }
      await axios.put(`${API}/projects/${projectId}`, payload);
      setDirty(false);
    } catch (error) {
      console.error('Error saving:', error);
    } finally {
      setSaving(false);
    }
  }, [projectId]);

  // Refs para el auto-save
  const modelRef = useRef(model);
  const behaviorsRef = useRef(behaviors);
  useEffect(() => {
    modelRef.current = model;
  }, [model]);
  useEffect(() => {
    behaviorsRef.current = behaviors;
  }, [behaviors]);

  // Actualizar modelo (con auto-save + snapshot al historial)
  const updateModel = useCallback((newModel) => {
    setModel(newModel);
    if (!skipHistoryRef.current) {
      pushHistory(newModel, behaviorsRef.current);
    }
    scheduleSave();
  }, [scheduleSave, pushHistory]);

  const updateBehaviors = useCallback((newBehaviors) => {
    setBehaviors(newBehaviors);
    if (!skipHistoryRef.current) {
      pushHistory(modelRef.current, newBehaviors);
    }
    scheduleSave();
  }, [scheduleSave, pushHistory]);

  // ============================================================
  // Undo / Redo
  // ============================================================
  const undo = useCallback(() => {
    const h = historyRef.current;
    if (h.pointer <= 0) return false;
    const newPointer = h.pointer - 1;
    const snap = h.stack[newPointer];
    if (!snap) return false;
    skipHistoryRef.current = true;
    setModel(snap.model);
    setBehaviors(snap.behaviors);
    historyRef.current = { stack: h.stack, pointer: newPointer };
    setHistoryVersion((v) => v + 1);
    scheduleSave();
    // liberar el skip en el próximo tick
    setTimeout(() => { skipHistoryRef.current = false; }, 0);
    return true;
  }, [scheduleSave]);

  const redo = useCallback(() => {
    const h = historyRef.current;
    if (h.pointer >= h.stack.length - 1) return false;
    const newPointer = h.pointer + 1;
    const snap = h.stack[newPointer];
    if (!snap) return false;
    skipHistoryRef.current = true;
    setModel(snap.model);
    setBehaviors(snap.behaviors);
    historyRef.current = { stack: h.stack, pointer: newPointer };
    setHistoryVersion((v) => v + 1);
    scheduleSave();
    setTimeout(() => { skipHistoryRef.current = false; }, 0);
    return true;
  }, [scheduleSave]);

  const canUndo = historyRef.current.pointer > 0;
  const canRedo = historyRef.current.pointer < historyRef.current.stack.length - 1;
  // usar historyVersion para forzar cálculo tras cambio (no se usa directamente pero mantiene reactividad)
  void historyVersion;

  // Actualizar info del proyecto
  const updateProjectInfo = useCallback(async (updates) => {
    if (!projectId) return;
    try {
      const response = await axios.put(`${API}/projects/${projectId}`, updates);
      setProject(response.data);
    } catch (error) {
      console.error('Error updating project info:', error);
    }
  }, [projectId]);

  // Cleanup timer al desmontar
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
    };
  }, []);

  return {
    project,
    model,
    behaviors,
    loading,
    saving,
    dirty,
    updateModel,
    updateBehaviors,
    updateProjectInfo,
    saveNow,
    undo,
    redo,
    canUndo,
    canRedo,
  };
};
